import { Box } from "@mui/material";
import { ContactType, ProjectType } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import {
	BoundAsyncSelect,
	BoundTextField,
	useAsyncSelectContactQuery,
	useAsyncSelectMembersOfQuery,
} from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect, useRef } from "react";

import type { ProjectEditProps } from "./ProjectEditProps";
import type { Project } from "../types/ProjectTypes";

export const ProjectContactSection = (props: ProjectEditProps) => {
	const { t } = useTranslation();
	const { project, setProject, errors, setErrors } = props;

	const useBinding = <TValue,>(selector: (m: Project) => TValue) => {
		return useBoundDeep(project, setProject, selector, errors, setErrors);
	};

	const projectClientField = useBinding((p) => p.contactInformation.client);
	const projectContractorField = useBinding((p) => p.contactInformation.contractor);

	const searchContractorHook = useAsyncSelectContactQuery([ContactType.Contractor]);
	const searchClientHook = useAsyncSelectContactQuery([ContactType.Client]);

	const projectClientContactPersonField = useBinding(
		(p) => p.contactPersonInformation.clientContactPerson,
	);
	const projectContractorContactPersonField = useBinding(
		(p) => p.contactPersonInformation.contractorContactPerson,
	);
	const searchClientContactPersonHook = useAsyncSelectMembersOfQuery(
		project.contactInformation.client?.id ?? null,
		[ContactType.ContactPerson, ContactType.Mailbox],
	);
	const searchContractorContactPersonHook = useAsyncSelectMembersOfQuery(
		project.contactInformation.contractor?.id ?? null,
		[ContactType.ContactPerson, ContactType.Mailbox],
	);

	const projectLeaderField = useBinding((p) => p.contactPersonInformation.contractorProjectLeader);
	const searchLeaderHook = useAsyncSelectMembersOfQuery(
		project.contactInformation.contractor?.id ?? null,
	);

	// Use refs to avoid adding the entire bound field objects as dependencies
	const clientContactPersonFieldRef = useRef(projectClientContactPersonField);
	const clientSearchHookRef = useRef(searchClientContactPersonHook);
	const contractorContactPersonFieldRef = useRef(projectContractorContactPersonField);
	const contractorSearchHookRef = useRef(searchContractorContactPersonHook);
	const projectLeaderFieldRef = useRef(projectLeaderField);
	const searchLeaderHookRef = useRef(searchLeaderHook);

	// Track previous values to detect actual changes
	const prevClientIdRef = useRef(project.contactInformation.client?.id);
	const prevContractorIdRef = useRef(project.contactInformation.contractor?.id);

	// Update refs on every render to always have the latest values
	clientContactPersonFieldRef.current = projectClientContactPersonField;
	clientSearchHookRef.current = searchClientContactPersonHook;
	contractorContactPersonFieldRef.current = projectContractorContactPersonField;
	contractorSearchHookRef.current = searchContractorContactPersonHook;

	useEffect(() => {
		const currentClientId = projectClientField.value?.id;
		const prevClientId = prevClientIdRef.current;

		// On initial mount: just initialize the search
		if (prevClientId === undefined) {
			clientSearchHookRef.current.resetSearch();
		}
		// On value change: reset search and clear the contact person
		else if (currentClientId !== prevClientId) {
			clientContactPersonFieldRef.current.onChangeValue(null);
			clientSearchHookRef.current.resetSearch();
		}

		// Update previous value for next comparison
		prevClientIdRef.current = currentClientId;
	}, [projectClientField.value?.id]);

	useEffect(() => {
		const currentContractorId = projectContractorField.value?.id;
		const prevContractorId = prevContractorIdRef.current;

		// On initial mount: just initialize the search
		if (prevContractorId === undefined) {
			contractorSearchHookRef.current.resetSearch();
			searchLeaderHookRef.current.resetSearch();
		}
		// On value change: reset search and clear the contact persons
		else if (currentContractorId !== prevContractorId) {
			contractorContactPersonFieldRef.current.onChangeValue(null);
			contractorSearchHookRef.current.resetSearch();
			projectLeaderFieldRef.current.onChangeValue(null);
			searchLeaderHookRef.current.resetSearch();
		}

		// Update previous value for next comparison
		prevContractorIdRef.current = currentContractorId;
	}, [projectContractorField.value?.id]);

	const projectEndCustomerField = useBinding((p) => p.contactInformation.endCustomer ?? "");

	const isInternal = project.projectType === ProjectType.Internal;

	return (
		<>
			{!isInternal && (
				<>
					<div className="hrTitle lightGreyFont paddingBottom40">
						<span className="left">{t("client").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<div>
						<Box className="fullCenter">
							<BoundAsyncSelect
								label={t("clients")}
								boundedValue={projectClientField}
								searchHook={searchClientHook}
								additionalProps={{ className: "w-100 left" }}
							/>
						</Box>
						<Box className="fullCenter" sx={{ mt: 2.5 }}>
							<BoundAsyncSelect
								label={t("client-contacts")}
								boundedValue={projectClientContactPersonField}
								searchHook={searchClientContactPersonHook}
								additionalProps={{ className: "w-100 left" }}
							/>
						</Box>
					</div>
				</>
			)}
			<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
				<span className="left">{t("contractor").toLocaleUpperCase()}</span>
				<hr className="right" />
			</div>
			<div>
				<Box className="fullCenter">
					<BoundAsyncSelect
						label={t("contractors")}
						boundedValue={projectContractorField}
						searchHook={searchContractorHook}
						additionalProps={{ className: "w-100 left" }}
					/>
				</Box>
				<Box className="fullCenter" sx={{ mt: 2.5 }}>
					<BoundAsyncSelect
						label={t("contractor-contacts")}
						boundedValue={projectContractorContactPersonField}
						searchHook={searchContractorContactPersonHook}
						additionalProps={{ className: "w-100 left" }}
					/>
				</Box>
				<Box className="fullCenter" sx={{ mt: 2.5 }}>
					<BoundAsyncSelect
						label={t("project-leader")}
						boundedValue={projectLeaderField}
						searchHook={searchLeaderHook}
						additionalProps={{ className: "w-100 left" }}
					/>
				</Box>
			</div>
			{!isInternal && (
				<>
					<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
						<span className="left">{t("end-customer").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<div className="paddingBottom40">
						<Box className="fullCenter">
							<BoundTextField
								label={t("end-customer")}
								boundedValue={projectEndCustomerField}
								additionalProps={{
									sx: { float: "left", width: "100%" },
									variant: "outlined",
								}}
							/>
						</Box>
					</div>
				</>
			)}
		</>
	);
};
